import { type CurrencyCode, type SeriesDataPoint } from "@doitintl/cmp-models";
import { darken, lighten, useTheme } from "@mui/material/styles";
import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import { formatValueWithCurrency } from "../../Components/hooks/useFormatter";
import { ThemeModes } from "../../muiThemeTypes";
import { getCurrencySymbol } from "../../utils/common";

interface ChartSeriesOptions {
  name: string;
  data: { x: number; y: number }[];
  type: string;
  color: string;
  dashStyle?: string;
  marker?: {
    enabled: boolean;
  };
}

interface ChartProps {
  commitmentSeries: SeriesDataPoint[];
  marketplaceSeries: SeriesDataPoint[];
  totalSeries: SeriesDataPoint[];
  forecastSeries?: SeriesDataPoint[];
  currencyCode: CurrencyCode;
  marketplaceLimitAmount?: number;
}

export const CommitmentChart = ({
  commitmentSeries,
  marketplaceSeries,
  totalSeries,
  forecastSeries,
  currencyCode,
  marketplaceLimitAmount,
}: ChartProps) => {
  const theme = useTheme();
  const currencySymbol = getCurrencySymbol(currencyCode);

  const chartSeries: ChartSeriesOptions[] = [
    {
      name: "Total eligible",
      data: totalSeries.map((point) => ({ x: point.date.toDate().getTime(), y: point.value })),
      color: theme.palette.info.light,
      type: "area",
    },
    {
      name: "Commitment",
      data: commitmentSeries.map((point) => ({ x: point.date.toDate().getTime(), y: point.value })),
      type: "line",
      color:
        theme.palette.mode === ThemeModes.LIGHT
          ? lighten(theme.palette.text.disabled, 0.2)
          : darken(theme.palette.text.disabled, 0.4),
    },
  ];

  if (forecastSeries && forecastSeries.length > 0) {
    chartSeries.push({
      name: "Forecast",
      data: forecastSeries.map((point) => ({ x: point.date.toDate().getTime(), y: point.value })),
      type: "line",
      dashStyle: "ShortDash",
      color:
        theme.palette.mode === ThemeModes.LIGHT
          ? lighten(theme.palette.text.disabled, 0.2)
          : darken(theme.palette.text.disabled, 0.4),
      marker: {
        enabled: false,
      },
    });
  }

  const hasNonZeroMarketplaceData = marketplaceSeries.some((point) => point.value > 0);
  if (hasNonZeroMarketplaceData) {
    chartSeries.splice(1, 0, {
      name: "Marketplace",
      data: marketplaceSeries.map((point) => ({ x: point.date.toDate().getTime(), y: point.value })),
      type: "area",
      color: theme.palette.success.main,
    });
  }

  return (
    <HighchartsReact
      highcharts={Highcharts}
      allowChartUpdate={true}
      options={{
        chart: {
          height: 320,
          backgroundColor: theme?.palette?.background.paper,
        },
        exporting: {
          enabled: false,
        },
        title: {
          text: "",
        },
        xAxis: {
          left: 38,
          type: "datetime",
          labels: {
            style: {
              color: theme?.palette?.text?.primary,
            },
          },
          accessibility: {
            rangeDescription: "",
          },
        },
        yAxis: {
          title: {
            text: "",
          },
          labels: {
            style: {
              color: theme?.palette?.text?.primary,
            },
          },
          plotLines:
            marketplaceLimitAmount && marketplaceLimitAmount > 0
              ? [
                  {
                    value: marketplaceLimitAmount,
                    color: theme.palette.divider,
                    width: 3,
                    zIndex: 3,
                    label: {
                      text: `${formatValueWithCurrency(marketplaceLimitAmount, 0, currencyCode, true)} Marketplace limit`,
                      align: "left",
                      style: {
                        color: theme.palette.text.secondary,
                      },
                    },
                  },
                ]
              : [],
        },
        tooltip: {
          shared: true,
          pointFormat: `<span style="color:{point.color}">\u25CF<b> {series.name}</b></span> : {point.y}<br/>`,
          valuePrefix: currencySymbol,
          valueDecimals: 0,
        },
        credits: {
          enabled: false,
        },
        plotOptions: {
          series: {
            animation: true,
          },
          area: {
            fillOpacity: 0.35,
          },
        },
        series: chartSeries,
        legend: {
          layout: "horizontal",
          enabled: true,
          itemStyle: {
            color: theme.palette.text.secondary,
            fontWeight: 400,
          },
        },
      }}
    />
  );
};
